//@flow
/**
 * Learner reducer
 * @module learner/reducer
 */
import { Record, List, Map } from 'immutable'
import { getProposedQuizId } from './lib'

export const UPDATE_LEARNER_NOTES = 'UPDATE_LEARNER_NOTES'
export const UPDATE_LEARNER_NOTES_REQUEST = 'UPDATE_LEARNER_NOTES_REQUEST'
export const UPDATE_LEARNER_NOTES_SUCCESS = 'UPDATE_LEARNER_NOTES_SUCCESS'
export const UPDATE_LEARNER_NOTES_FAILURE = 'UPDATE_LEARNER_NOTES_FAILURE'

export const SUBMIT_LEARNER_FORM_REQUEST = 'SUBMIT_LEARNER_FORM_REQUEST'
export const SUBMIT_LEARNER_FORM_PENDING = 'SUBMIT_LEARNER_FORM_PENDING'
export const SUBMIT_LEARNER_FORM_SUCCESS = 'SUBMIT_LEARNER_FORM_SUCCESS'
export const SUBMIT_LEARNER_FORM_FAILURE = 'SUBMIT_LEARNER_FORM_FAILURE'

export const SUBMIT_ANSWER_REQUEST = 'SUBMIT_ANSWER_REQUEST'
export const SUBMIT_ANSWER_PENDING = 'SUBMIT_ANSWER_PENDING'
export const SUBMIT_ANSWER_SUCCESS = 'SUBMIT_ANSWER_SUCCESS'
export const SUBMIT_ANSWER_FAILURE = 'SUBMIT_ANSWER_FAILURE'
export const LOADING_PAGE = 'LOADING_PAGE'
export const HIDE_ERROR_SUBMISSION = 'HIDE_ERROR_SUBMISSION'

export const FINISH_QUIZ_LATER_REQUEST = 'FINISH_QUIZ_LATER_REQUEST'
export const FINISH_QUIZ_LATER_SUCCESS = 'FINISH_QUIZ_LATER_SUCCESS'
export const FINISH_QUIZ_LATER_FAILURE = 'FINISH_QUIZ_LATER_FAILURE'

export const RESTORE_LEARNER_REQUEST = 'RESTORE_LEARNER_REQUEST'
export const RESTORE_LEARNER_SUCCESS = 'RESTORE_LEARNER_SUCCESS'
export const RESTORE_LEARNER_FAILURE = 'RESTORE_LEARNER_FAILURE'

export const UPDATE_DLRQ_LEARNER = 'UPDATE_DLRQ_LEARNER'
export const UPDATE_DLRQ_LEARNER_SUCCESS = 'UPDATE_DLRQ_LEARNER_SUCCESS'
export const UPDATE_DLRQ_LEARNER_FAILURE = 'UPDATE_DLRQ_LEARNER_FAILURE'

export const COMPLETE_DLRQ_LEARNER = 'COMPLETE_DLRQ_LEARNER'
export const COMPLETE_DLRQ_LEARNER_SUCCESS = 'COMPLETE_DLRQ_LEARNER_SUCCESS'
export const COMPLETE_DLRQ_LEARNER_FAILURE = 'COMPLETE_DLRQ_LEARNER_FAILURE'

export type Action =
  | { type: 'UPDATE_LEARNER_NOTES', payload: string }
  | { type: 'UPDATE_LEARNER_NOTES_REQUEST' }
  | { type: 'UPDATE_LEARNER_NOTES_SUCCESS', payload: string }
  | { type: 'UPDATE_LEARNER_NOTES_FAILURE', payload: Error, error: true }
  | { type: 'SUBMIT_LEARNER_FORM_REQUEST' }
  | { type: 'SUBMIT_LEARNER_FORM_PENDING', payload: LearnerDetailsSubmission }
  | { type: 'SUBMIT_LEARNER_FORM_SUCCESS', payload: Learner }
  | { type: 'SUBMIT_LEARNER_FORM_FAILURE', payload: Error, error: true }
  | { type: 'SUBMIT_ANSWER_REQUEST' }
  | { type: 'SUBMIT_ANSWER_PENDING', payload: QuestionSubmission }
  | {
      type: 'SUBMIT_ANSWER_SUCCESS',
      payload: {
        response?: { nextQuestionId: string },
        questionSubmission: QuestionSubmission
      }
    }
  | { type: 'SUBMIT_ANSWER_FAILURE', payload: Error, error: true }
  | { type: 'FINISH_QUIZ_LATER_REQUEST' }
  | { type: 'FINISH_QUIZ_LATER_SUCCESS' }
  | { type: 'FINISH_QUIZ_LATER_FAILURE', payload: Error, error: true }
  | { type: 'RESTORE_LEARNER_REQUEST' }
  | { type: 'RESTORE_LEARNER_SUCCESS', payload: Learner | false }
  | { type: 'RESTORE_LEARNER_FAILURE', payload: Error, error: true }
  | { type: 'PENDING_QUESTION_SUBMISSIONS_SCHEDULE', payload: Object }
  | { type: 'PENDING_QUESTION_SUBMISSIONS_SUBMITTING' }
  | { type: 'PENDING_QUESTION_SUBMISSIONS_CLEAR' }
  | { type: 'PENDING_QUESTION_SUBMISSIONS_CLEAR_SUCCESS' }
  | { type: 'UPDATE_DLRQ_LEARNER' }
  | { type: 'COMPLETE_DLRQ_LEARNER'}

export const LearnerRecord = Record({
  pendingFormSubmission: null,
  pendingQuestionSubmissions: List(),
  submittingAnswer: false,
  loading: false,
  formCompeted: false,
  id: '',
  invite: '',
  notes: '',
  currentQuestionId: '',
  testLevel: getProposedQuizId(),
  email: '',
  learnerId: '',
  name: {
    first: '',
    last: ''
  },
  details: List(),
  answers: List(),
  complete: false,
  dlrqScore: {
    capability: 0,
    access: 0,
    baseline: 0,
    accessBaseline: 0,
    focusAreaBaseline: 0,
    focusAreas: [0, 0, 0, 0, 0]
  },
  dlrqWorkings: {   // workings just for use during development to display workings on screen for Dave
    capability: [],
    access: [],
    focusAreas: [[], [], [], [], []]
  },
  dlrqResponses: List(),
  submissionFailed: false,
  selectedCourseIdComparison: '',
  course: {
    _id: '',
    title: ''
  },
  invitedBy: ''
})

/**
 * handles storing and fetching of learner data
 * @memberof learner/reducer
 */
export default function LearnerReducer(
  state: LearnerRecord = new LearnerRecord(),
  action: Action
): LearnerRecord {
  switch (action.type) {
    case RESTORE_LEARNER_REQUEST: {
      return state.merge({
        loading: true
      })
    }
    case RESTORE_LEARNER_FAILURE: {
      return state.merge({
        loading: false
      })
    }
    case RESTORE_LEARNER_SUCCESS: {
      if (!action.payload) return state
      // if there are pending question submissions then don't modify the
      // currentQuestionId or answers array, as this may be out of sync with the server
      let mergeAnswers = true
      let mergeCurrentQuestionId = true
      if (state.pendingQuestionSubmissions.size > 0) {
        mergeAnswers = false
        mergeCurrentQuestionId = false
      }

      console.log("action.payload", action.payload);

      return mergeLearner({
        state,
        learner: action.payload,
        mergeAnswers,
        mergeCurrentQuestionId
      })
    }

    case SUBMIT_LEARNER_FORM_PENDING: {
      const { payload: learnerDetails } = action
      return state.merge({
        submissionFailed: false,
        pendingFormSubmission: learnerDetails,
        formCompeted: true,
        currentQuestionId: learnerDetails.initialQuestionRequest,
        testLevel: learnerDetails.quizId,
        learnerId: learnerDetails.learnerId.value,
        name: {
          first: learnerDetails.firstname.value,
          last: learnerDetails.lastname.value
        },
        email: learnerDetails.email.value,
        details: learnerDetails.custom.map(({ _id, value, skipped }) => ({
          id: _id,
          value: skipped ? 'skipped' : value
        })),
        answers: [],
        invitedBy: learnerDetails.invitedBy,
      })
    }

    case SUBMIT_LEARNER_FORM_SUCCESS: {
      return mergeLearner({
        state,
        learner: action.payload,
        pendingFormSubmission: null,
        mergeAnswers: false,
        mergeCurrentQuestionId: false
      })
    }

    case UPDATE_LEARNER_NOTES:
      return state.set('notes', action.payload)

    case LOADING_PAGE:
        return state.set('submittingAnswer', true)

    case SUBMIT_ANSWER_PENDING: {
      const questionSubmission = action.payload
      const { answer, nextQuestionRequest } = questionSubmission
      const answers = state
        .get('answers')
        .filter(({ questionId }) => answer.questionId !== questionId)
        .concat({
          questionId: answer.questionId,
          answer
        })
      return state
        .set('answers', answers)
        .update('pendingQuestionSubmissions', pendingQuestionSubmissions =>
          pendingQuestionSubmissions.concat(questionSubmission)
        )
        .set('currentQuestionId', nextQuestionRequest)
        .set('submittingAnswer', false)
    }

    case SUBMIT_ANSWER_SUCCESS: {
      const {
        questionSubmission: { answer }
      } = action.payload
      return state.update(
        'pendingQuestionSubmissions',
        pendingQuestionSubmissions =>
          pendingQuestionSubmissions.filter(
            ({ answer: { questionId } }) => answer.questionId !== questionId
          )
      )
    }

    case SUBMIT_ANSWER_FAILURE: {
      return state
            .set('submittingAnswer', false)
            .set('submissionFailed', true)
    }
    case COMPLETE_DLRQ_LEARNER_FAILURE: {
      return state.set('submissionFailed', true)
    }

    case HIDE_ERROR_SUBMISSION: {
      return state.set('submissionFailed', false)
    }

    case UPDATE_DLRQ_LEARNER: {
      const { answer, nextQuestionId, dlrqScore, dlrqWorkings } = action.payload

      return state
        .set('currentQuestionId', nextQuestionId)
        .set('dlrqScore', dlrqScore)
        .set('dlrqWorkings', dlrqWorkings)
        .update('dlrqResponses', dlrqResponses => dlrqResponses.concat(answer))
    }

    case COMPLETE_DLRQ_LEARNER_SUCCESS: {
      return state
        .set('complete', true)
    }

    default: {
      return state
    }
  }
}

/**
 * Merges a learner into the provided state.
 * @memberof learner/reducer
 */
function mergeLearner({
  state,
  learner: {
    _id,
    inviteCode,
    state: { currentQuestion },
    testLevel,
    notes,
    learnerId,
    name,
    email,
    details,
    answers,
    complete,
    selectedCourseIdComparison,
    course,
    invitedBy
  },
  clearPendingForm = false,
  mergeAnswers = true,
  mergeCurrentQuestionId = true
}: {
  state: LearnerRecord,
  learner: Learner,
  clearPendingForm?: boolean,
  mergeAnswers?: boolean,
  mergeCurrentQuestionId?: boolean
}) {
  const newState: {
    ...Learner,
    currentQuestionId?: string,
    answers?: Array<LearnerAnswer>,
    details: Array<{ id: string, value: string }>,
    pendingFormSubmission?: null | LearnerDetailsSubmission
  } = {
    loading: false,
    id: _id,
    invite: inviteCode,
    testLevel,
    notes,
    learnerId,
    name,
    email,
    complete,
    selectedCourseIdComparison,
    course,
    invitedBy,
    details: details.map(({ formElement: id, value }) => ({ id, value }))
  }
  if (clearPendingForm) {
    newState.pendingFormSubmission = null
  }
  if (mergeCurrentQuestionId) {
    newState.currentQuestionId = currentQuestion
  }
  if (mergeAnswers) {
    newState.answers = answers.map(answer => ({
      questionId: answer.questionId,
      answer: {
        answer: answer.answer,
        correct: answer.correct
      }
    }))
  }
  return state.merge(newState)
}



// WEBPACK FOOTER //
// ./src/state/learner/reducer.js